import { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_PAYMENT_METHODS } from '../../../config/constants';
import { analytics } from '../../../services/analytics';
import { firebaseAuth } from '../../../services/firebase';
import { intercom } from '../../../services/intercom';
import { ldClient, identifyUser, useFlag } from '../../../services/launchDarkly';
import { isBingoCardsPackage } from '../../../stores/bingo/helpers';
import { useStores } from '../../../stores/useStores';
import { assumeForeignHtmlStyle, isPaymentsAppMessage } from '../../../utils/payment-app.utils';
import { SKRILL_SESSION_ID_STORAGE_KEY } from '../../payments/constants';
import { isSubscriptionPackage } from '../v2/types.utils';

type V5MessageRequest = {
  type: 'getFlags' | 'getIdToken' | 'updateLDContext' | 'getPaymentMethods';
  payload?: unknown;
  source?: string;
  requestId?: string;
};

export function useMessagesListener(
  setIframeStyle: (style: { height?: number | string }) => void,
  setIframeLoading: (loading: boolean) => void,
  isMiniStore?: boolean,
  setIsBottomSheet?: (bottomSheet: boolean) => void,
) {
  const { bingoStore, uiStore, payRedeemStore, portalStore, appStore, eventTriggerStore } = useStores();

  const paymentMethods = useFlag('payment_methods', DEFAULT_PAYMENT_METHODS);

  const isForeign = useRef(false);

  const onMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      if (!isPaymentsAppMessage(event.data)) {
        return;
      }

      if ('action' in event.data) {
        if (event.data.action === 'close') {
          portalStore.closePortal();
          payRedeemStore.awaitForTransaction();
        }

        return;
      }

      if (!('class' in event.data)) {
        isForeign.current = true;
        return;
      }

      switch (event.data.class) {
        case 'analytics':
          if (event.data.type === 'event' && event.data.details) {
            void analytics.sendEvent(event.data.details.name, event.data.details.params);
          }
          isForeign.current = false;
          return;

        case 'flow': {
          switch (event.data.type) {
            case 'size-change':
              if (!isForeign.current) {
                setIframeStyle({ height: event.data.details?.height });
              }
              return;
            case 'loading-finished':
              setIframeLoading(false);
              return;
            case 'sheet-mode':
              setIsBottomSheet?.(!!event.data.details?.bottomSheet);
              return;
            case 'back-to-lobby': {
              const onClose = portalStore.options.onClose;

              portalStore.closePortal();

              if (onClose && typeof onClose === 'function') {
                onClose();
                return;
              }

              uiStore.setCloseDialog();
              return;
            }
            case 'exit': {
              const onClose = portalStore.options.onClose;

              const { eventTriggerData } = appStore.currentOrderPackage || {};

              if (onClose && typeof onClose === 'function') {
                portalStore.closePortal();
                onClose();
                return;
              }

              if (uiStore.openDialog?.options?.onClose && typeof uiStore.openDialog.options.onClose === 'function') {
                uiStore.openDialog.options.onClose?.();
              } else if (eventTriggerData?.activeEvent) {
                portalStore.closePortal();
                uiStore.setCloseDialog();
                void eventTriggerStore.openPopup(eventTriggerData?.activeEvent);
                return;
              }

              const closeDialogAnd = (next?: () => void) => {
                portalStore.closePortal();
                uiStore.setCloseDialog();
                if (next) next();
              };

              if (isMiniStore) {
                closeDialogAnd(() => uiStore.setIsVisibleMiniStore(true));
                return;
              }

              if (
                isSubscriptionPackage(appStore.selectedPackage) ||
                (appStore.selectedPackage && isBingoCardsPackage(appStore.selectedPackage))
              ) {
                closeDialogAnd(() => {
                  bingoStore.openBingoStore();
                });
                return;
              }

              closeDialogAnd(() => uiStore.setOpenFullPageDialog('store'));
              return;
            }
            // Crown Rips shipping completed (fee charged + delivery created) — nudge the game
            // to refresh its vault; the portal closes via the flow's own exit.
            case 'ship-success': {
              payRedeemStore.bumpGameUiShipSuccess();
              return;
            }
            case 'success': {
              const details = event.data.details || {
                amount: appStore.selectedPackage?.price,
                transactionId: '',
                isFirstDeposit: false,
              };
              const { amount, transactionId, isFirstDeposit, paymentMethod, actualGrantedRewards } = details;
              if (transactionId) {
                payRedeemStore.setInProcessTransactionId(transactionId);
                payRedeemStore.transactionsEventsRegistry.set(transactionId, {
                  amount: amount ?? appStore.selectedPackage?.price ?? 0,
                  isFirstDeposit,
                  transactionId,
                  paymentMethod,
                  selectedPackage: appStore.selectedPackage ?? undefined,
                  actualGrantedRewards,
                });
              }
              portalStore.closePortal();
              payRedeemStore.handleSuccessPurchase({ successDialogType: 'v2' });
              appStore.removeNpuStore();
              return;
            }
            case 'redirect': {
              isForeign.current = true;
              const styles = assumeForeignHtmlStyle(event.data.details?.url);
              setIframeStyle(styles);
              return;
            }
            case 'buttonAction':
              if (event.data.details?.kind === 'support') {
                intercom.handleOpenSupport();
              }
              return;
            case 'skrillSessionStarted':
              if (event.data.details) {
                sessionStorage.setItem(SKRILL_SESSION_ID_STORAGE_KEY, event.data.details.sessionId);
              }
              return;
          }
        }
      }
    },
    [
      portalStore,
      payRedeemStore,
      setIframeLoading,
      setIsBottomSheet,
      bingoStore,
      uiStore,
      setIframeStyle,
      appStore,
      isMiniStore,
      eventTriggerStore,
    ],
  );

  const handleV5Request = useCallback(
    async (event: MessageEvent) => {
      const data = event.data as V5MessageRequest;

      if (!data || typeof data !== 'object' || !('type' in data)) {
        return;
      }

      if (
        data.type !== 'getFlags' &&
        data.type !== 'getIdToken' &&
        data.type !== 'updateLDContext' &&
        data.type !== 'getPaymentMethods'
      ) {
        return;
      }

      const { requestId } = data;

      switch (data.type) {
        case 'getFlags': {
          const allFlags = ldClient?.allFlags() || {};
          const response = {
            type: 'getFlags_response',
            requestId,
            details: {
              flags: allFlags,
            },
          };

          if (event.source && 'postMessage' in event.source) {
            event.source.postMessage(response, { targetOrigin: event.origin });
          }
          break;
        }

        case 'getIdToken': {
          try {
            const token = await firebaseAuth.currentUser?.getIdToken();

            if (!token) {
              console.error('[Player Frontend] Could not get Firebase token for payments v5');
              return;
            }

            const response = {
              type: 'getIdToken_response',
              requestId,
              details: {
                token,
              },
            };

            if (event.source && 'postMessage' in event.source) {
              event.source.postMessage(response, { targetOrigin: event.origin });
            }
          } catch (error) {
            console.error('[Player Frontend] Error getting ID token for payments v5:', error);
          }
          break;
        }

        case 'updateLDContext': {
          try {
            const contextUpdates = (data.payload || {}) as Record<string, unknown>;

            if (!ldClient) {
              console.error('[Player Frontend] LaunchDarkly client not initialized');
              return;
            }

            const currentContext = ldClient.getContext();
            const updatedContext = {
              ...currentContext,
              ...contextUpdates,
            };

            await identifyUser(updatedContext);

            const updatedFlags = ldClient.allFlags() || {};

            const response = {
              type: 'updateLDContext_response',
              requestId,
              details: {
                flags: updatedFlags,
              },
            };

            if (event.source && 'postMessage' in event.source) {
              event.source.postMessage(response, { targetOrigin: event.origin });
            }
          } catch (error) {
            console.error('[Player Frontend] Error updating LD context for payments v5:', error);
          }
          break;
        }

        case 'getPaymentMethods': {
          try {
            const response = {
              type: 'getPaymentMethods_response',
              requestId,
              details: {
                paymentMethods,
              },
            };
            if (event.source && 'postMessage' in event.source) {
              event.source.postMessage(response, { targetOrigin: event.origin });
            }
          } catch (error) {
            console.error('[Player Frontend] Error getting payment methods for payments v5:', error);
          }
          break;
        }
      }
    },
    [paymentMethods],
  );

  useEffect(() => {
    const wrappedMessageHandler = (event: MessageEvent) => {
      void handleV5Request(event);
      onMessage(event);
    };

    window.addEventListener('message', wrappedMessageHandler);
    return () => {
      window.removeEventListener('message', wrappedMessageHandler);
    };
  }, [onMessage, handleV5Request, uiStore]);
}
